import React from 'react';
import { SectionCard } from '../../components/SectionCard';

interface SettingListProps {
    children: React.ReactNode;
}

/**
 * The sections of one settings destination.
 *
 * Save and Discard used to sit at the bottom of the last section; they are on
 * the floating save bar now, the same control the product editor uses, so the
 * page is only the thing being edited.
 */
const SettingList: React.FC<SettingListProps> & { Section: typeof Section } = ({ children }) => (
    <div className="space-y-5">{children}</div>
);

interface SectionProps {
    title: string;
    description: string;
    children: React.ReactNode;
    /**
     * Formerly drew the rule under the section. Panels have their own edges,
     * so it does nothing now; kept so the call sites did not all have to
     * change in the same commit.
     */
    showFooterActions?: boolean;
    /** Anchor target, so a rail on the same page can jump to this section. */
    id?: string;
}

/*
  A panel, like every other section in the app.

  The design draws settings as gutter rows — the section name and its
  explanation in a column to the left of the fields — and that is what was
  built. It is also the one place in the app where fields sit on the bare page
  instead of in a panel: the product editor, the order, the payouts and the
  storefront are all panels. Put the settings rail beside a gutter row and the
  page is three columns wide, with the fields squeezed into the last of them.

  Raised in review: "there's some confusion around whether we have panels or we
  have everything on the surface ... we probably need to make use of panels in
  there for that to work", and "we've sort of got these three columns there
  that look a bit odd". This is a deliberate departure from the spike on Dan's
  instruction, not drift — worth saying, because the same collapse was tried
  once before as my own idea and reverted in round three for being exactly
  that. The explanation moves inside the panel, under its title, where it still
  reads before the fields it explains.
*/
const Section: React.FC<SectionProps> = ({ title, description, children, id }) => (
    <SectionCard title={title} className="scroll-mt-24" testId={id ? `settings-section-${id}` : undefined}>
        <div id={id} className="scroll-mt-24">
            <p className="-mt-1 mb-4 max-w-prose text-xs leading-5 text-muted-foreground">
                {description}
            </p>
            <div className="grid grid-cols-1 gap-x-5 gap-y-5 sm:grid-cols-6">{children}</div>
        </div>
    </SectionCard>
);

SettingList.Section = Section;

export default SettingList;
